import moment from 'moment';

export const findPreviousFinancialYear = (currentFinancialYearId, listFYData = []) => {
  if (!currentFinancialYearId || !listFYData?.length) return null;

  const currentFY = listFYData.find((fy) => fy.financialyearid === currentFinancialYearId);
  if (!currentFY) return null;

  const prevStartYear = Number(currentFY.startyear) - 1;
  const prevEndYear = Number(currentFY.endyear) - 1;

  const prevFY =
    listFYData.find(
      (f) =>
        Number(f.startyear) === prevStartYear &&
        Number(f.endyear) === prevEndYear &&
        (currentFY.companyid ? f.companyid === currentFY.companyid : true)
    ) || listFYData.find((f) => Number(f.startyear) === prevStartYear && Number(f.endyear) === prevEndYear);

  return prevFY || null;
};

export const determinePeriodType = (periodData, listFYData = []) => {
  if (!periodData?.startdate || !periodData?.enddate || !periodData?.financialyearid) return null;

  const fy = listFYData.find((f) => f.financialyearid === periodData.financialyearid);
  if (!fy) return null;

  const start = moment.unix(periodData.startdate);
  const end = moment.unix(periodData.enddate);

  if (periodData.startdate === fy.startdate && periodData.enddate === fy.enddate) {
    return { periodType: 'fullyear', periodValue: 'Full Year' };
  }

  if (start.isSame(end, 'month') && start.isSame(start.clone().startOf('month'), 'day') && end.isSame(end.clone().endOf('month'), 'day')) {
    return { periodType: 'monthly', periodValue: start.format('MMMM') };
  }

  const startTs = start.startOf('day').unix();
  const endTs = end.endOf('day').unix();

  const fyStart = moment.unix(fy.startdate);
  const fyStartQuarter = fyStart.clone().startOf('quarter');
  
  const quarterStart = start.clone().startOf('quarter');
  const quarterEnd = start.clone().endOf('quarter');
  
  const quarterStartTs = quarterStart.startOf('day').unix();
  const quarterEndTs = quarterEnd.endOf('day').unix();
  
  if (startTs === quarterStartTs && endTs === quarterEndTs) {
    const monthsFromFYStart = start.diff(fyStartQuarter, 'months', true);
    const quarterNum = Math.floor(monthsFromFYStart / 3) + 1;
    
    if (quarterNum > 0 && quarterNum <= 4) {
      return { periodType: 'quarterly', periodValue: `Q${quarterNum}` };
    }
  }

  const fyStartMoment = moment.unix(fy.startdate);
  const h1Start = fyStartMoment.clone().startOf('month');
  const h1End = h1Start.clone().add(5, 'months').endOf('month');
  const h2Start = h1End.clone().add(1, 'day').startOf('month');
  const h2End = h2Start.clone().add(5, 'months').endOf('month');

  const h1StartTs = h1Start.startOf('day').unix();
  const h1EndTs = h1End.endOf('day').unix();
  const h2StartTs = h2Start.startOf('day').unix();
  const h2EndTs = h2End.endOf('day').unix();

  if (startTs === h1StartTs && endTs === h1EndTs) {
    return { periodType: 'halfyearly', periodValue: 'H1' };
  }
  if (startTs === h2StartTs && endTs === h2EndTs) {
    return { periodType: 'halfyearly', periodValue: 'H2' };
  }

  return { periodType: 'custom', periodValue: null };
};

export const getPreviousYearPeriod = (currentPeriod, listFYData = []) => {
  if (!currentPeriod?.startdate || !currentPeriod?.enddate || !currentPeriod?.financialyearid) return null;

  const prevFY = findPreviousFinancialYear(currentPeriod.financialyearid, listFYData);
  if (!prevFY) return null;

  const periodInfo = determinePeriodType(currentPeriod, listFYData);
  if (!periodInfo) return null;

  const { periodType, periodValue } = periodInfo;

  if (periodType === 'fullyear') {
    return {
      startdate: prevFY.startdate,
      enddate: prevFY.enddate,
      financialyearid: prevFY.financialyearid,
      companyid: prevFY.companyid,
      openingbalance: currentPeriod.openingbalance || 1,
    };
  }

  if (periodType === 'monthly' && periodValue) {
    const currentStart = moment.unix(currentPeriod.startdate);
    const currentFY = listFYData.find((f) => f.financialyearid === currentPeriod.financialyearid);
    if (!currentFY) return null;

    const currentFYStart = moment.unix(currentFY.startdate);
    
    const monthsFromFYStart = currentStart.diff(currentFYStart, 'months', true);
    const monthOffset = Math.floor(monthsFromFYStart);

    const prevFYStart = moment.unix(prevFY.startdate);
    const prevMonthStart = prevFYStart.clone().add(monthOffset, 'months').startOf('month');
    const prevMonthEnd = prevMonthStart.clone().endOf('month');

    if (prevMonthStart.unix() >= prevFY.startdate && prevMonthEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevMonthStart.unix(),
        enddate: prevMonthEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  if (periodType === 'quarterly' && periodValue) {
    const currentStart = moment.unix(currentPeriod.startdate);
    const currentFY = listFYData.find((f) => f.financialyearid === currentPeriod.financialyearid);
    if (!currentFY) return null;

    const currentFYStart = moment.unix(currentFY.startdate);
    const currentFYStartQuarter = currentFYStart.clone().startOf('quarter');
    
    const monthsFromFYStartQuarter = currentStart.diff(currentFYStartQuarter, 'months', true);
    const quartersFromFYStartQuarter = Math.floor(monthsFromFYStartQuarter / 3);

    const prevFYStart = moment.unix(prevFY.startdate);
    const prevFYStartQuarter = prevFYStart.clone().startOf('quarter');
    const prevQuarterStart = prevFYStartQuarter.clone().add(quartersFromFYStartQuarter * 3, 'months').startOf('quarter');
    const prevQuarterEnd = prevQuarterStart.clone().endOf('quarter');

    if (prevQuarterStart.unix() >= prevFY.startdate && prevQuarterEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevQuarterStart.unix(),
        enddate: prevQuarterEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  if (periodType === 'halfyearly' && periodValue) {
    const prevFYStart = moment.unix(prevFY.startdate);
    let prevPeriodStart, prevPeriodEnd;

    if (periodValue === 'H1') {
      prevPeriodStart = prevFYStart.clone().startOf('month');
      prevPeriodEnd = prevPeriodStart.clone().add(5, 'months').endOf('month');
    } else if (periodValue === 'H2') {
      prevPeriodStart = prevFYStart.clone().add(6, 'months').startOf('month');
      prevPeriodEnd = prevPeriodStart.clone().add(5, 'months').endOf('month');
    }

    if (prevPeriodStart && prevPeriodEnd && prevPeriodStart.unix() >= prevFY.startdate && prevPeriodEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevPeriodStart.unix(),
        enddate: prevPeriodEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  if (periodType === 'custom') {
    const currentStart = moment.unix(currentPeriod.startdate);
    const currentEnd = moment.unix(currentPeriod.enddate);
    const currentFY = listFYData.find((f) => f.financialyearid === currentPeriod.financialyearid);
    if (!currentFY) return null;

    const currentFYStart = moment.unix(currentFY.startdate);
    const currentFYEnd = moment.unix(currentFY.enddate);
    
    const currentFYDuration = currentFYEnd.diff(currentFYStart, 'days');
    const daysFromFYStart = currentStart.diff(currentFYStart, 'days');
    const daysFromFYEnd = currentFYEnd.diff(currentEnd, 'days');
    
    const periodDuration = currentEnd.diff(currentStart, 'days');

    const prevFYStart = moment.unix(prevFY.startdate);
    const prevFYEnd = moment.unix(prevFY.enddate);
    const prevFYDuration = prevFYEnd.diff(prevFYStart, 'days');

    const relativeStartPosition = daysFromFYStart / currentFYDuration;
    const prevPeriodStart = prevFYStart.clone().add(Math.floor(prevFYDuration * relativeStartPosition), 'days');
    
    const prevPeriodEnd = prevPeriodStart.clone().add(periodDuration, 'days');

    if (prevPeriodStart.unix() >= prevFY.startdate && prevPeriodEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevPeriodStart.unix(),
        enddate: prevPeriodEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }

    const currentStartMonth = currentStart.month();
    const currentStartDate = currentStart.date();
    const currentEndMonth = currentEnd.month();
    const currentEndDate = currentEnd.date();

    const prevStartCandidate = prevFYStart.clone().month(currentStartMonth).date(currentStartDate);
    const prevEndCandidate = prevFYStart.clone().month(currentEndMonth).date(currentEndDate);

    if (prevStartCandidate.unix() < prevFY.startdate) {
      prevStartCandidate.add(1, 'year');
    }
    if (prevEndCandidate.unix() < prevFY.startdate) {
      prevEndCandidate.add(1, 'year');
    }
    if (prevStartCandidate.unix() > prevFY.enddate) {
      prevStartCandidate.subtract(1, 'year');
    }
    if (prevEndCandidate.unix() > prevFY.enddate) {
      prevEndCandidate.subtract(1, 'year');
    }

    if (
      prevStartCandidate.unix() >= prevFY.startdate &&
      prevStartCandidate.unix() <= prevFY.enddate &&
      prevEndCandidate.unix() >= prevFY.startdate &&
      prevEndCandidate.unix() <= prevFY.enddate &&
      prevStartCandidate.unix() <= prevEndCandidate.unix()
    ) {
      return {
        startdate: prevStartCandidate.unix(),
        enddate: prevEndCandidate.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  return null;
};

